import {
    CustomCrescentPartsTrainingActions,
    CustomCrescentPartsTrainingState,
} from "@crescentParts/Store/CrescentPartsTraining/TrainingsReducer";
import { Handler } from "@crescentParts/Types/CrescentHandlerCreator";
import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";

type Parameter = {
    trainings: any[];
    queryParams: any;
    initialLoad: boolean;
    numberOfRanges: number;
    allFilters?: any;
};

type CustomHandlerType = Handler<
    Parameter,
    Parameter,
    CustomCrescentPartsTrainingActions,
    CustomCrescentPartsTrainingState
>;

type AppliedFilter = {
    filterNames: string[];
    filterType: string;
};

type AppliedFilters = AppliedFilter[];

const excludedParameters = [
    "CurrentBillToId",
    "CurrentShipToId",
    "SetContextLanguageCode",
    "SetContextCurrencyCode",
    "access_token",
];

const priceFilterOptions = [
    {
        name: "50-100",
        filterType: "price",
    },
    {
        name: "101-200",
        filterType: "price",
    },
    {
        name: "201-300",
        filterType: "price",
    },
];

const generatePriceLimit = (price: string, ranges: any) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(Number(price))) {
        return "";
    }
    let priceLimits = "";
    ranges?.forEach((range: any) => {
        if (+price >= range.min && +price <= range.max) {
            priceLimits = range.name;
        }
    });
    return priceLimits;
};

const generateDynamicFilters = (trainings: any, queryParams: any, numberOfRanges: number) => {
    // Generate dynamic filter options
    const typeFilters = generateFilterArray("trainingType", trainings, queryParams);
    const locationFilters = generateFilterArray("location", trainings, queryParams);
    const peuFilters = generateFilterArray("peu", trainings, queryParams);
    const restrictionFilters = generateFilterArray("restriction", trainings, queryParams);
    const { priceFilters } = generatePriceFilterArray(trainings, numberOfRanges);
    return {
        typeFilters,
        locationFilters,
        priceFilters,
        peuFilters,
        restrictionFilters,
    };
};

const generateFilterArray = (filterType: string, trainings: any, queryParams: any): any => {
    const filterSet = new Set<string>();
    const filterArr: any = [];
    trainings.forEach((training: any) => {
        if (training?.fields[filterType]) {
            filterSet.add(training?.fields[filterType]);
        }
    });

    filterSet.forEach((item: string) => {
        filterArr.push({ name: item, filterType });
    });
    return filterArr;
};

const getMinMaxPrices = (trainings: any) => {
    let min = Infinity;
    let max = 0; 
    
    trainings.forEach((training: any) => {
        const trainingPrice = Number(training.fields.price);
        if (trainingPrice < min) { min = trainingPrice; }
        if (trainingPrice > max) { max = trainingPrice; }
    });
    return { min, max };
};

const generatePriceRanges = (min: number, max: number, numberOfRanges = 5) => {   
    const rangeSize = Math.ceil((max - min) / numberOfRanges);   
    const ranges = [];   
    for(let i = 0; i < numberOfRanges; i++) {  
        const rangeMin = min + i * rangeSize;    
        const rangeMax = i === numberOfRanges - 1 ? max : rangeMin + rangeSize;    
        ranges.push({ 
            name: `${rangeMin}-${rangeMax}`,
            min: rangeMin,
            max: rangeMax,
        });
    }
    return ranges; 
};

const generatePriceFilterArray = (trainings: any, numberOfRanges: number) => {
    const {min, max } = getMinMaxPrices(trainings);
    const ranges = generatePriceRanges(min, max, numberOfRanges);
    
    const filterOptions = [...ranges];
    const filterArr: any = [];
    filterOptions.forEach((item: any) => {
        filterArr.push({ name: item.name, filterType: "price" });
    });
    return { priceFilters: filterArr, ranges};
};

const applyFilters = (queryParams: any, trainings: any[], numberOfRanges: number) => {
    const appliedFilters: AppliedFilters = [];
    const selectedFilterNamesArr: string[] = [];

    const filteredQueryParams = Object.keys(queryParams).filter(
        (key) => !excludedParameters.includes(key)
    ).reduce((acc: { [key: string]: any }, key: string) => {
        acc[key] = queryParams[key];
        return acc;
    }, {});

    for (const key in filteredQueryParams) {
        const filterNames = filteredQueryParams[key].toLowerCase().split("_");
        appliedFilters.push({ filterType: key, filterNames });
        filterNames.forEach((item: string) => {
            selectedFilterNamesArr.push(item);
        });
    }
    const filteredTrainings =
        selectedFilterNamesArr.length === 0 ? [...trainings] : filterTrainings(appliedFilters, trainings, numberOfRanges);
    return {
        selectedFilterNamesArr,
        appliedFilters,
        filteredTrainings,
    };
};

const filterTrainings = (appliedFilters: string | any[], trainings: any[], numberOfRanges: any) => {
    const { priceFilters, ranges } = generatePriceFilterArray(trainings, numberOfRanges);
    const updatedFilteredTrainings = trainings.filter(function (item) {
        let result = true;
        for (let i = 0; i < appliedFilters.length; i++) {
            if (appliedFilters[i].filterNames.length > 0 && appliedFilters[i]["filterType"] === "price") {
                const priceLimit = generatePriceLimit(item.fields.price, ranges);
                if (
                    appliedFilters[i]["filterNames"].findIndex(
                        (val: string) => val.toLowerCase() === priceLimit.toLowerCase(),
                    ) === -1
                ) {
                    return false;
                }
            }
            if (
                appliedFilters[i].filterNames.length > 0 &&
                appliedFilters[i]["filterType"] !== "price" &&
                appliedFilters[i]["filterNames"].findIndex(
                    (val: string) => val.toLowerCase() === item?.fields[appliedFilters[i]["filterType"]]?.toLowerCase(),
                ) === -1
            ) {
                result = false;
            }
        }
        return result;
    });
    return updatedFilteredTrainings;
};

export const DispatchGenerateDynamicFilters: CustomHandlerType = props => {
    const { trainings, queryParams, initialLoad, numberOfRanges } = props.parameter;
    if (initialLoad) {
        (props as unknown as any).dispatch({
            type: "CrescentPartsTraining/GenerateDynamicFilters",
            payload: generateDynamicFilters(trainings, queryParams, numberOfRanges),
        });
    }
};

export const DispatchApplyFilters: CustomHandlerType = props => {
    const { trainings, queryParams, numberOfRanges } = props.parameter;
    props.dispatch({
        type: "CrescentPartsTraining/ApplyFiltersFromUrl",
        payload: applyFilters(queryParams, trainings, numberOfRanges),
    });
};

export const chain = [DispatchGenerateDynamicFilters, DispatchApplyFilters];

const setFilters = createHandlerChainRunner(chain, "SetFilters");
export default setFilters;
